import type { TuitionPayment } from '@/types';
import { formatDate, formatNumber } from '@/lib/datetime';

interface TuitionPaymentHistoryPanelProps {
  payments: TuitionPayment[];
  deletingId: string;
  onDelete: (payment: TuitionPayment) => void;
}

export default function TuitionPaymentHistoryPanel({
  payments,
  deletingId,
  onDelete,
}: TuitionPaymentHistoryPanelProps) {
  const sorted = [...payments].sort((a, b) => (a.cycle_due < b.cycle_due ? 1 : -1));
  const total = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="mt-5 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
              <i className="ri-receipt-line"></i>
            </span>
            납부 기록
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            회차별로 납부한 금액과 날짜를 따로 저장해요. 금액이 바뀌어도 이전 기록은 그대로 남아요.
          </p>
        </div>
        <span className="rounded-full bg-background-200 px-3 py-1 text-[11px] font-semibold text-foreground-600">
          총 {payments.length}건 · {formatNumber(total)}원
        </span>
      </div>

      {sorted.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-6 text-center text-xs text-foreground-500">
          아직 납부 기록이 없어요. 결제완료로 표시하면 회차별 기록이 쌓여요.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {sorted.map((payment) => (
            <li
              key={payment.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className="ri-check-line"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950">
                    {formatNumber(payment.amount)}원
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {formatDate(payment.cycle_due)} 회차 · {formatDate(payment.paid_at)} 납부
                    {payment.memo ? ` · ${payment.memo}` : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDelete(payment)}
                disabled={deletingId === payment.id}
                className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-semibold text-foreground-600 transition hover:bg-background-200 disabled:opacity-50 whitespace-nowrap"
              >
                {deletingId === payment.id ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-delete-bin-line"></i>
                )}
                삭제
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}